import {
  resolveVero1RecommendationName,
  Vero1BannerState,
  Vero1Recommendation,
} from "shared/interfaces/api/vero1";

/** Matches the backend `vero1_review_status` filter facets. */
export const VERO1_REVIEW_STATUS = {
  PENDING_REVIEW: "pending_review",
  UNDER_REVIEW: "under_review",
  RECOMMENDATION_SHOWN: "recommendation_shown",
} as const;

export type Vero1ReviewStatus =
  (typeof VERO1_REVIEW_STATUS)[keyof typeof VERO1_REVIEW_STATUS];

export const VERO1_REVIEW_STATUS_FILTER_OPTIONS: {
  key: Vero1ReviewStatus;
  label: string;
}[] = [
  { key: VERO1_REVIEW_STATUS.PENDING_REVIEW, label: "Pending review" },
  { key: VERO1_REVIEW_STATUS.UNDER_REVIEW, label: "Under review" },
  {
    key: VERO1_REVIEW_STATUS.RECOMMENDATION_SHOWN,
    label: "Reviewed by VERO1",
  },
];

/** Lumen keys aren't statically known, so only non-string junk is dropped. */
export const sanitizeRecommendationStatusFilter = (
  statuses?: string[] | null
): string[] =>
  (statuses ?? []).filter(
    (status): status is string =>
      typeof status === "string" && status.length > 0
  );

const sentenceCase = (label: string): string =>
  label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();

export const buildRecommendationStatusOptions = (
  vero1FiltersActive: boolean,
  lumenOptions: { key: string; label: string }[]
): { key: string; label: string }[] =>
  vero1FiltersActive
    ? [
        ...VERO1_REVIEW_STATUS_FILTER_OPTIONS,
        ...lumenOptions.map(({ key, label }) => ({
          key,
          label: sentenceCase(label),
        })),
      ]
    : [];

const RECOMMENDATION_LABELS: Record<Vero1Recommendation, string> = {
  [Vero1Recommendation.Agree]: "AGREE",
  [Vero1Recommendation.Approve]: "APPROVE",
  [Vero1Recommendation.Conditional]: "CONDITIONAL",
  [Vero1Recommendation.Decline]: "DECLINE",
  [Vero1Recommendation.Cancel]: "CANCEL",
};

const HOLD_LABELS = {
  pending_review: "PENDING REVIEW",
  under_review: "UNDER REVIEW",
} as const;

export interface ResolvedVero1ListState {
  label: string;
  subtext: string | null;
}

/** null when the deal isn't VERO1-governed (caller falls back to the Lumen score). */
export const resolveVero1ListState = (
  bannerState?: Vero1BannerState | null
): ResolvedVero1ListState | null => {
  if (!bannerState) {
    return null;
  }
  switch (bannerState.state) {
    case "pending_review":
      return { label: HOLD_LABELS.pending_review, subtext: null };
    case "under_review":
      return { label: HOLD_LABELS.under_review, subtext: null };
    // override_not_applied isn't governing — folds into pending review
    case "override_not_applied":
      return { label: HOLD_LABELS.pending_review, subtext: null };
    // an applicant outstanding withholds the submitted review the same way
    case "awaiting_applicants":
      return { label: HOLD_LABELS.pending_review, subtext: null };
    case "recommendation":
      return {
        // the client's configured word is what the review decided; the enum
        // label only carries reviews with no configured name. Resolved by the
        // same helper the banner uses, so the two surfaces cannot disagree.
        label:
          resolveVero1RecommendationName(bannerState) ??
          (bannerState.recommendation
            ? RECOMMENDATION_LABELS[bannerState.recommendation]
            : "REVIEWED"),
        subtext: "By VERO1",
      };
    case "unable_to_verify_identity":
      return { label: "CANCELLED", subtext: "Unable to verify identity" };
    default:
      return null;
  }
};
